<template>
  <TableCard
    ref="datatable"
    v-model:sort="sorting"
    v-model:page="page"
    v-model:page-size="pageSize"
    v-model:period="period"
    class="ui-flex-grow"
    :header="$t('paymentHistory.balanceHistory')"
    :loading="loading"
    :columns="columns"
    :sort-by-name="sortByName"
    :sort-dir-name="sortDirName"
    :rows="rows"
    :currency="currencyLabel"
    :show-total="false"
    :count="historyCount"
    :formaters="formaters"
    period-picker
    dont-fill-empty
    extend-height
    i18n-path="reports"
    @set-range="period = $event"
    @change-range="fetchDataTable"
    @fetch-data="fetchDataTable"
  >
    <template v-slot:actionsAppend>
      <div class="checkbox payment-history__checkbox ui-12 ui-mobile-12 ui-g-md">
        <input
          id="checkbox1"
          v-model="payouts"
          type="checkbox"
        >
        <label
          class="checkbox-label"
          :class="{
            payouts
          }"
          for="checkbox1"
        >
          {{ $t('paymentHistory.showOnlyPayouts') }}
        </label>
      </div>
    </template>
  </TableCard>
</template>

<script>
import moment from 'moment';
import Formaters from '@/views/Dashboard/CustomReports/formaters';
import TableCard from '@/components/Cards/TableCard.vue';
import UrlMixin from '@/components/Query/url-mixin';
import UrlMixinHooks from '@/components/Query/url-mixin-hooks';
import InjectPlugins from '@/components/Report/Components/inject-plugins';
import HelpersMixin from '@/components/Helpers/helpers-mixin';
import { pageSizeMixin, resolvePageSize } from '@/views/Dashboard/Reports/page_size';
import tableFlowMixinGenerator, { getDefaultPeriod } from '@/views/Dashboard/Reports/tableFlowMixin';
import formatCurrency from '@/mixins/format-currency';
import { fixRange } from '@/views/Dashboard/Reports/dateMixin';

const sizes = {
  1080: 15,
  1440: 25,
};
const viewName = '/wallet';
const pageLimitProp = 'pageSize';
const pageSize = resolvePageSize(viewName, {
  _default: 10,
  sizes,
});

const defaultParams = {
  page: 1,
  pageSize,
  sort_column: 'date',
  sort_dir: 'desc',
  transaction_type: undefined,
  payouts: false,
};

const defaultPeriod = getDefaultPeriod();

const fetch = async (ctx, params) => {
  if ((params && params.sort_column === 'index' && params.sort_dir === 'asc') || !(params.sort_column && params.sort_dir)) {
    params.sort_column = 'date';
    params.sort_dir = 'desc';
  }

  const start_timestamp = params && params.start_timestamp
    ? params.start_timestamp
    : ctx.period
      ? ctx.period.from
      : params.period
        ? params.period.from
        : defaultPeriod.from;

  const end_timestamp = params && params.end_timestamp
    ? params.end_timestamp
    : ctx.period
      ? ctx.period.to
      : params.period
        ? params.period.to
        : defaultPeriod.to;

  const period = {
    period: params.period?.period || params.period || ctx.period?.period || 30,
    from: start_timestamp,
    to: end_timestamp,
  };

  const fixed = fixRange(period);

  if (fixed) {
    period.from = fixed.from;
    period.to = fixed.to;
  }

  const query = ctx.params ? ctx.params.lastQuery : {};
  const getWallets = ctx.$store.dispatch('getWallets');

  if (ctx.lastQuery) {
    ctx.lastQuery = {
      ...params,
      ...query,
      from: ctx.period.from,
      to: ctx.period.to,
      period: ctx.period.period,
    };
  }

  await getWallets;

  const id = ctx.$store.state.payments.wallets[0].id;
  const data = await ctx.$api.getWalletHistory(id, {
    start_timestamp: period.from,
    end_timestamp: period.to,
    limit: ctx.pageSize || pageSize,
    offset: 0,
    ...params,
  });

  ctx.history = data?.payload || [];
  ctx.historyCount = data?.misc?.count || 0;
};

const tableFlowMixin = tableFlowMixinGenerator({
  defaultParams: {
    ...defaultParams,
  },
  params: Object.keys(defaultParams),
  fetch,
  period: 'param',
  windowRefreshHook: true,
  fromName: 'start_timestamp',
  toName: 'end_timestamp',
  sortByName: 'sort_column',
  sortDirName: 'sort_dir',
});

export default {
  name: 'BalanceHistoryTable',
  fetch,
  components: {
    TableCard,
  },
  mixins: [
    UrlMixin,
    UrlMixinHooks,
    InjectPlugins,
    HelpersMixin,
    pageSizeMixin,
    tableFlowMixin,
    formatCurrency,
  ],
  props: ['passedParams'],
  emits: ['fetch'],
  data() {
    return {
      viewName,
      pageLimitProp,
      payouts: false,
      loading: false,
      columns: [
        {
          name: 'index',
          sortable: true,
          filterable: false,
          show: false,
        }, {
          name: 'date',
          sortable: 'custom',
          filterable: false,
          format: 'format-date',
        }, {
          name: 'transaction_type',
          sortable: false,
          filterable: false,
        }, {
          name: 'to_hold',
          sortable: false,
          filterable: false,
          format: 'ncurrency',
          align: 'right',
        }, {
          name: 'to_available',
          sortable: false,
          filterable: false,
          format: 'ncurrency',
          align: 'right',
        }, {
          name: 'hold_balance',
          sortable: false,
          filterable: false,
          format: 'ncurrency',
          align: 'right',
        }, {
          name: 'balance',
          sortable: false,
          filterable: false,
          format: 'ncurrency',
          align: 'right',
        },
      ],
      period: defaultPeriod,
      history: [],
      historyCount: 0,
      totalRows: 0,
      pagination: true,
      transaction_type: undefined,
      formaters: Formaters,
      isTableReady: false,
    };
  },
  beforeMount() {
    this.pageSize = this.limit;
  },
  computed: {
    currencyLabel() {
      return this.getCurrencyLabel(this.$store.state.payments.currencyCode);
    },
    rows() {
      return this.history.map((e, i) => ({
        index: i,
        date: moment(e.grouping)
          .format('YYYY-MM-DD'),
        transaction_type: this.formatType(e.transaction_type),
        to_available: e.amount_to_available,
        to_hold: e.amount_to_hold,
        hold_balance: e.result_balance_hold,
        balance: e.result_balance_available,
      }));
    },
  },
  created() {
    this.setFromQuery(this.passedParams?.balanceHistory || this.passedParams);
  },
  async mounted() {
    this.fetchData();
  },
  watch: {
    payouts(value, oldValue) {
      if (value === oldValue) {
        return;
      }

      if (value) {
        this.transaction_type = ['withdraw', 'withdraw_abort'];
      } else {
        this.transaction_type = undefined;
      }

      let params;
      if (this.pagination) {
        params = {
          sort_column: this.sorting.sort_column,
          sort_dir: this.sorting.sort_dir,
          limit: this.pageSize,
          offset: 0,
          transaction_type: this.transaction_type,
          start_timestamp: this.period.from,
          end_timestamp: this.period.to,
        };
      }
      if (params.sort_column === 'index' && params.sort_dir === 'asc') {
        params.sort_column = 'date';
        params.sort_dir = 'desc';
      }

      this.fetchData(params);
    },
  },
  methods: {
    cacheQuery() {
      this.pushQuery(this.$route, {
        balanceHistory: this.cacheData,
        paymentHistory: this.urlQuery.paymentHistory,
      });
    },
    async getFetchDataParams(query) {
      const params = {
        ...query,
        transaction_type: this.payouts
          ? ['withdraw', 'withdraw_abort']
          : undefined,
      };

      this.$emit('fetch');

      return params;
    },
    formatType(type) {
      return this.$t(`paymentHistory.transaction_type_${type}`);
    },
  },
};
</script>
